/**
 * Tutors API + TanStack Query hooks.
 * Endpoint catalog: docs/API-CONTRACT.md → "Tutors".
 *
 * Backend (Workspace/backend/tutors/):
 *   GET    /tutors/                    → paginated list (TutorListSerializer)
 *   POST   /tutors/                    → create User + Tutor in one txn
 *   GET    /tutors/{id}/               → detail (TutorDetailSerializer)
 *   PATCH  /tutors/{id}/               → update
 *   DELETE /tutors/{id}/               → soft delete (is_active=False)
 *   POST   /tutors/{id}/upload-cv/     → multipart, field "cv"
 *   POST   /tutors/{id}/upload-dbs/    → multipart, field "dbs"
 *   POST   /tutors/{id}/upload-id/     → multipart, field "national_id"
 *   GET    /tutors/{id}/students/      → students assigned to the tutor
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api } from "./client";
import type { StudentListItem } from "./students";
import { toQueryString, type ListQuery, type Paginated } from "./types";

/* ─────────────────────────────────────────── Types */

export interface TutorListItem {
  id: number | string;
  full_name?: string;
  email?: string;
  avatar?: string | null;
  subjects?: string[];
  /** Key stage codes taught: ks2, ks3, ks4 (GCSE), ks5 (A-Level). */
  levels?: string[];
  is_active?: boolean;
  lessons_delivered?: number;
  created_at?: string;
  updated_at?: string;
  external_id?: string | null;
}

export interface TutorDetailUser {
  id: number | string;
  email: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  phone?: string;
  avatar?: string | null;
}

export interface TutorDetail {
  id: number | string;
  user: TutorDetailUser;
  subjects: string[];
  levels?: string[];
  /** Shown to families. No contact details: the server filters them. */
  bio?: string;
  /** Mandatory items still empty (field names, plus "avatar"). */
  missing?: string[];
  date_of_birth?: string | null;
  contact_number?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  postcode?: string;
  country?: string;
  /** Auth-checked API paths (open with the file viewer). Admin or the tutor only. */
  cv?: string | null;
  national_id?: string | null;
  dbs?: string | null;
  cv_name?: string | null;
  national_id_name?: string | null;
  dbs_name?: string | null;
  bank_account_name?: string;
  bank_name?: string;
  bank_account_number?: string;
  bank_sort_code?: string;
  lessons_delivered?: number;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  external_id?: string | null;
}

export interface TutorListQuery extends ListQuery {
  is_active?: boolean | string;
  subjects?: number | string;
}

export interface TutorCreatePayload {
  email: string;
  first_name?: string;
  last_name?: string;
  password?: string;
  phone?: string;
  subjects?: number[];
  levels?: number[];
  bio?: string;
  date_of_birth?: string | null;
  contact_number?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  postcode?: string;
  country?: string;
  bank_account_name?: string;
  bank_name?: string;
  bank_account_number?: string;
  bank_sort_code?: string;
  is_active?: boolean;
}

/** Admin edits may change name, email and account phone. Never the password. */
export type TutorUpdatePayload = Partial<Omit<TutorCreatePayload, "password">>;

export type TutorUploadField = "cv" | "dbs" | "id" | "photo";

/* ─────────────────────────────────────────── Raw API calls */

export function listTutors(
  q: TutorListQuery = {},
): Promise<Paginated<TutorListItem>> {
  return api
    .get<Paginated<TutorListItem>>(`/tutors/${toQueryString(q)}`)
    .then((r) => r.data);
}

export function getTutor(id: number | string): Promise<TutorDetail> {
  return api.get<TutorDetail>(`/tutors/${id}/`).then((r) => r.data);
}

export function createTutor(
  payload: TutorCreatePayload,
): Promise<TutorDetail> {
  return api.post<TutorDetail>(`/tutors/`, payload).then((r) => r.data);
}

export function updateTutor(
  id: number | string,
  payload: TutorUpdatePayload,
): Promise<TutorDetail> {
  return api.patch<TutorDetail>(`/tutors/${id}/`, payload).then((r) => r.data);
}

export function deleteTutor(id: number | string): Promise<void> {
  return api.delete<void>(`/tutors/${id}/`).then(() => undefined);
}

export function uploadTutorFile(
  id: number | string,
  field: TutorUploadField,
  file: File,
): Promise<TutorDetail> {
  const fieldName = field === "id" ? "national_id" : field;
  const fd = new FormData();
  fd.append(fieldName, file);
  // URL slug uses "id" not "national_id" (see views.py @action url_path).
  return api
    .post<TutorDetail>(`/tutors/${id}/upload-${field}/`, fd)
    .then((r) => r.data);
}

/**
 * Detail cache key. A deep link hands over the id as a string ("2") and a
 * list row as a number (2); normalising here keeps them one cache entry, so
 * an edit refreshes whichever copy the drawer is showing.
 */
function detailKey(id: number | string | null | undefined) {
  return ["tutors", "detail", id == null ? null : String(id)] as const;
}

export function listTutorStudents(
  id: number | string,
): Promise<Paginated<StudentListItem>> {
  return api
    .get<Paginated<StudentListItem>>(`/tutors/${id}/students/?page_size=100`)
    .then((r) => r.data);
}

/* ─────────────────────────────────────────── Hooks */

export function useTutors(
  q: TutorListQuery = {},
  enabled = true,
): UseQueryResult<Paginated<TutorListItem>> {
  return useQuery({
    queryKey: ["tutors", q],
    queryFn: () => listTutors(q),
    enabled,
    // Search, filter and "Show more" keep the current list on screen.
    placeholderData: keepPreviousData,
  });
}

export function useTutorDetail(
  id: number | string | null | undefined,
  enabled = true,
): UseQueryResult<TutorDetail> {
  return useQuery({
    queryKey: detailKey(id),
    queryFn: () => getTutor(id as number | string),
    enabled: enabled && id != null,
  });
}

/** Students assigned to one tutor (`GET /tutors/{id}/students/`). */
export function useTutorStudents(
  id: number | string | null | undefined,
  enabled = true,
): UseQueryResult<Paginated<StudentListItem>> {
  return useQuery({
    queryKey: ["tutors", "students", id == null ? null : String(id)],
    queryFn: () => listTutorStudents(id as number | string),
    enabled: enabled && id != null,
  });
}

export function useCreateTutor(): UseMutationResult<
  TutorDetail,
  Error,
  TutorCreatePayload
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TutorCreatePayload) => createTutor(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tutors"] });
    },
  });
}

export function useUpdateTutor(
  id: number | string,
): UseMutationResult<TutorDetail, Error, TutorUpdatePayload> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TutorUpdatePayload) => updateTutor(id, payload),
    onSuccess: (data) => {
      qc.setQueryData(detailKey(id), data);
      void qc.invalidateQueries({ queryKey: ["tutors"] });
    },
  });
}

export function useDeleteTutor(): UseMutationResult<
  void,
  Error,
  number | string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => deleteTutor(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tutors"] });
    },
  });
}

export function useUploadTutorFile(
  id: number | string,
  field: TutorUploadField,
): UseMutationResult<TutorDetail, Error, File> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadTutorFile(id, field, file),
    onSuccess: (data) => {
      qc.setQueryData(detailKey(id), data);
      void qc.invalidateQueries({ queryKey: ["tutors"] });
    },
  });
}

/** Labels for the mandatory items a tutor profile may still be missing. */
export const TUTOR_MISSING_LABELS: Record<string, string> = {
  address_line1: "address line 1",
  city: "town or city",
  postcode: "postcode",
  country: "country",
  bank_account_name: "account name",
  bank_name: "bank name",
  bank_account_number: "account number",
  bank_sort_code: "sort code",
  cv: "CV",
  national_id: "photo ID",
  dbs: "DBS certificate",
  avatar: "profile photo",
  subjects: "subjects",
  levels: "levels",
};

export const TEACHING_LEVEL_CODES = ["ks2", "ks3", "ks4", "ks5"] as const;
export const TEACHING_LEVEL_LABELS: Record<string, string> = {
  ks2: "KS2",
  ks3: "KS3",
  ks4: "GCSE",
  ks5: "A-Level",
};
